import React, { useEffect, useState, useRef } from 'react';
import withAuth from '../Utils/withAuth';
import {
  Flex, Box, Input, Button, HStack, IconButton, Table, Thead, Tbody, Tr, Th, Td,
  Container, InputGroup, InputLeftElement, Spinner, useToast, Text, Link, Icon
} from '@chakra-ui/react';
import { Image } from '@chakra-ui/react';
import 'react-datepicker/dist/react-datepicker.css';
import searchicon from '../Images/icon/search.svg';
import Footer from '../Components/General/Footer';
import ContentHeader from '../Components/General/ContentHeader';
import { getRecipientShareList, getTransactionInfo, getHistoricPdfUrl } from '../Utils/api';
import BlockchainViewerModal from '../Components/Cert/BlockchainViewerModal.js';
import '../Styles/theme/blockchain_window.css';
import { ChevronDownIcon, ViewIcon, CloseIcon } from '@chakra-ui/icons';

const ThStyled = ({ children }) => (
  <Th
    fontFamily="Open Sans"
    fontWeight="600"
    fontSize="14px"
    lineHeight="150%"
    letterSpacing="1%"
    color="#00292B"
    verticalAlign="middle"
  >
    {children}
  </Th>
);

function RecipientShareManagement() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterTrigger, setDateFilterTrigger] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const toast = useToast();
  const activeStartDate = useRef('');
  const activeEndDate = useRef('');

  const placeNo = (sessionStorage.getItem('place_no') || 'PLACE_001').replace(/"/g, '');
  const companyNo = (sessionStorage.getItem('company_no') || 'COMP_001').replace(/"/g, '');

  // 區塊鏈瀏覽器相關狀態
  const [showModel3, setShowModel3] = useState(false);
  const [tx_place_no, setTxPlaceNo] = useState('');
  const [tx_company_no, setTxCompanyNo] = useState('');
  const [tx_paction_no, setTxPactionNo] = useState('');
  const [tx_TransactionHash, setTxHash] = useState('');
  const [tx_block_hash, setTxBlockHash] = useState('');
  const [tx_block_number, setTxBlockNumber] = useState('');
  const [tx_raw_data, setTxRawData] = useState('');
  const [tx_event_type, setTxEventType] = useState('');
  const [tx_share_id, setTxShareId] = useState(null);
  const [tx_open_id, setTxOpenId] = useState(null);
  const [tx_pdf_hash, setTxPdfHash] = useState('');

  const handleLinkClick = (transaction_hash, log_index) => {
    getTransactionInfo(transaction_hash, log_index)
      .then((response_data) => {
        setTxPlaceNo(response_data.place_no);
        setTxCompanyNo(response_data.company_no);
        setTxPactionNo(response_data.paction_no);
        setTxBlockHash(response_data.block_hash);
        setTxBlockNumber(response_data.block_number);
        setTxRawData(response_data.raw_data);
        setTxEventType(response_data.event_type || '');
        setTxShareId(response_data.share_id !== undefined ? response_data.share_id : null);
        setTxOpenId(response_data.open_id !== undefined ? response_data.open_id : null);
        setTxPdfHash(response_data.pdf_hash || '');
        setTxHash(transaction_hash);
        setShowModel3(true);
      })
      .catch((err) => {
        console.error("Failed to get transaction:", err);
        toast({ title: '取得鏈上資訊失敗', status: 'error', duration: 2000 });
      });
  };

  const reloadData = () => {
    setLoading(true);
    getRecipientShareList(placeNo, companyNo)
      .then((res) => {
        setRecords(res || []);
      })
      .catch((err) => {
        console.error('取得接收記錄失敗', err);
        toast({
          title: '載入失敗',
          description: '無法取得接收記錄',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadData();
  }, []);

  useEffect(() => {
    let result = [];
    if (filter === 'all') {
      result = [...records];
    } else if (filter === 'enabled') {
      result = records.filter(item => item.share_enable);
    } else if (filter === 'disabled') {
      result = records.filter(item => !item.share_enable);
    }

    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(item =>
        (item.paction_no && item.paction_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.sender_company_no && item.sender_company_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.sender_company_name && item.sender_company_name.toLowerCase().includes(lowercasedTerm)) ||
        (item.sender_place_no && item.sender_place_no.toLowerCase().includes(lowercasedTerm)) ||
        (item.sender_place_name && item.sender_place_name.toLowerCase().includes(lowercasedTerm))
      );
    }

    const formatDate = (str) => {
      try {
        return new Date(str.replace(/[\u4e00-\u9fa5]/g, '/').replace(/\//g, '-'));
      } catch (e) {
        console.error('日期格式錯誤:', str, e);
        return new Date(0);
      }
    };

    if (activeStartDate.current || activeEndDate.current) {
      if (activeStartDate.current && activeEndDate.current &&
        new Date(activeStartDate.current) > new Date(activeEndDate.current)) {
        toast({
          title: '日期錯誤',
          description: '起始日期不能晚於結束日期',
          status: 'warning',
          duration: 3000
        });
      } else {
        if (activeStartDate.current) {
          const start = new Date(activeStartDate.current);
          result = result.filter(item => {
            const itemDate = formatDate(item.share_time);
            return itemDate && itemDate >= start;
          });
        }
        if (activeEndDate.current) {
          const end = new Date(activeEndDate.current);
          end.setHours(23, 59, 59, 999);
          result = result.filter(item => {
            const itemDate = formatDate(item.share_time);
            return itemDate && itemDate <= end;
          });
        }
      }
    }

    result.sort((a, b) => {
      const dateA = formatDate(a.share_time);
      const dateB = formatDate(b.share_time);
      return dateB - dateA;
    });

    setFilteredRecords(result);
  }, [records, filter, searchTerm, dateFilterTrigger]);

  const handleFilter = (filterType) => {
    if (filter === filterType) {
      setFilter('all');
    } else {
      setFilter(filterType);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    activeStartDate.current = '';
    activeEndDate.current = '';
    setDateFilterTrigger(prev => prev + 1);
    toast({
      title: '已清除日期篩選',
      status: 'info',
      duration: 2000
    });
  };

  const handleApplyDateFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast({
        title: '日期錯誤',
        description: '起始日期不能晚於結束日期',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    activeStartDate.current = startDate;
    activeEndDate.current = endDate;
    setDateFilterTrigger(prev => prev + 1);

    if (startDate || endDate) {
      toast({
        title: '已應用日期篩選',
        status: 'success',
        duration: 2000
      });
    }
  };

  const exportToCSV = () => {
    try {
      const headers = [
        '接收時間',
        '狀態',
        '許可證號',
        '分享之運作人',
        '分享之運作場所'
      ].join(',');

      const csvRows = filteredRecords.map(item => {
        return [
          `"${item.share_time || ''}"`,
          `"${item.share_enable ? '啟用中' : '關閉'}"`,
          `"${item.paction_no || ''}"`,
          `"${item.sender_company_no || ''} / ${item.sender_company_name || ''}"`,
          `"${item.sender_place_no || ''} / ${item.sender_place_name || ''}"`,
        ].join(',');
      });

      const csvContent = [headers, ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const now = new Date();
      const fileName = `接收紀錄_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.csv`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: `匯出成功 (${filteredRecords.length} 筆記錄)`,
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('匯出CSV失敗', error);
      toast({
        title: '匯出失敗',
        description: '無法匯出CSV檔案',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Flex direction="column" w="100vw" minH="100vh" bg="#F4F6F7">
      <Box flex="1">
        <ContentHeader
          tabs={[
            { label: '我的證書', path: '/home' },
            { label: '分享管理', path: '/licenseshare' },
            { label: '接收紀錄', path: '/recipientshare' },
          ]}
        />

        <Container maxW="1440px" mt="40px" px={0}>
          <Flex justify="space-between" align="center" flexWrap="wrap">
            <HStack spacing="18px" flexGrow={1} alignItems="center">
              <InputGroup w="304px" h="40px">
                <InputLeftElement pointerEvents="none" height="100%">
                  <Image src={searchicon} boxSize="16px" alt="搜尋" />
                </InputLeftElement>
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="證件號碼、運作人、運作場所"
                  bg="white"
                  border="1px solid #A8B8C2"
                  borderRadius="3px"
                  fontSize="14px"
                  fontWeight="400"
                  color="#4F6470"
                  h="40px"
                  pl="32px"
                  _placeholder={{ color: '#4F6470' }}
                />
              </InputGroup>

              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                bg="white"
                border="1px solid #A8B8C2"
                borderRadius="3px"
                fontSize="14px"
                fontWeight="400"
                color="#4F6470"
                h="40px"
                w="160px"
                zIndex="1"
              />
              <Text color="#4F6470">~</Text>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                bg="white"
                border="1px solid #A8B8C2"
                borderRadius="3px"
                fontSize="14px"
                fontWeight="400"
                color="#4F6470"
                h="40px"
                w="160px"
                zIndex="1"
              />
              <Button
                size="sm"
                onClick={handleApplyDateFilter}
                colorScheme="teal"
                zIndex="1"
              >
                確定
              </Button>
              <Button
                size="sm"
                onClick={handleClearDates}
                zIndex="1"
              >
                清除
              </Button>

              <Button
                bg={filter === 'enabled' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('enabled')}
              >
                啟用中
              </Button>
              <Button
                bg={filter === 'disabled' ? "#B2F5B2" : "#DAE0E5"}
                color="#00292B"
                borderRadius="3px"
                w="73px"
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={() => handleFilter('disabled')}
              >
                已關閉
              </Button>
            </HStack>
            <HStack spacing={2}>
              <Button
                bg="#DAE0E5"
                color="#00292B"
                borderRadius="3px"
                px={4}
                h="29px"
                fontSize="14px"
                fontWeight="700"
                onClick={exportToCSV}
              >
                匯出
              </Button>
            </HStack>
          </Flex>
        </Container>

        <Container maxW="1440px" mt="16px" mb={10} px={0}>
          <Box overflowX="auto" bg="white" borderRadius="md">
            {loading ? (
              <Flex justify="center" align="center" p={6}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <Table>
                <Thead bg="#FFFFFF">
                  <Tr>
                    <ThStyled>接收時間</ThStyled>
                    <ThStyled>狀態</ThStyled>
                    <ThStyled>證件號碼</ThStyled>
                    <ThStyled>分享之運作人</ThStyled>
                    <ThStyled>分享之運作場所</ThStyled>
                    <ThStyled>分享 TX</ThStyled>
                    <ThStyled>證件 TX</ThStyled>
                    <ThStyled>開啟紀錄</ThStyled>
                    <ThStyled>操作</ThStyled>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredRecords.length === 0 ? (
                    <Tr>
                      <Td colSpan={9} textAlign="center" py={6}>
                        無符合條件的資料
                      </Td>
                    </Tr>
                  ) : (
                    filteredRecords.map((item) => {
                      const shareDate = item.share_time.substring(0, 10).replaceAll('/', '-');
                      const viewUrl = `/share/${item.paction_no}/${item.pdf_token}/${shareDate}`;
                      return (
                        <Tr key={item.id}>
                          <Td whiteSpace="nowrap">{item.share_time}</Td>
                          <Td whiteSpace="nowrap">
                            <Box
                              px={2}
                              py={1}
                              borderRadius="md"
                              bg={item.share_enable ? "green.50" : "gray.50"}
                              color={item.share_enable ? "green.600" : "gray.600"}
                              display="inline-block"
                              fontSize="sm"
                              fontWeight="bold"
                            >
                              {item.share_enable ? '啟用中' : '已關閉'}
                            </Box>
                          </Td>
                          <Td whiteSpace="nowrap">{item.paction_no}</Td>
                          <Td>
                            <Flex direction="column">
                              <Text>{item.sender_company_name}</Text>
                              {item.sender_company_no && <Text fontSize="sm" color="gray.500">{item.sender_company_no}</Text>}
                            </Flex>
                          </Td>
                          <Td>
                            <Flex direction="column">
                              <Text>{item.sender_place_name}</Text>
                              {item.sender_place_no && <Text fontSize="sm" color="gray.500">{item.sender_place_no}</Text>}
                            </Flex>
                          </Td>
                          <Td>
                            {item.share_tx ? (
                              <Link
                                color="teal.500"
                                textDecoration="underline"
                                onClick={() => handleLinkClick(item.share_tx)}
                                style={{ cursor: 'pointer' }}
                              >
                                {item.share_tx.substring(0, 10)}...
                              </Link>
                            ) : '無'}
                          </Td>
                          <Td>
                            {item.cert_tx ? (
                              <Link
                                color="teal.500"
                                textDecoration="underline"
                                onClick={() => handleLinkClick(item.cert_tx)}
                                style={{ cursor: 'pointer' }}
                              >
                                {item.cert_tx.substring(0, 10)}...
                              </Link>
                            ) : '無'}
                          </Td>
                          <Td>
                            {item.open_records && item.open_records.length > 0 ? (
                              <Button
                                size="xs"
                                rightIcon={<ChevronDownIcon />}
                                variant="outline"
                                onClick={() => setSelectedRecord(item)}
                              >
                                共 {item.open_records.length} 次開啟
                              </Button>
                            ) : (
                              <Text fontSize="sm" color="gray.500">尚未開啟</Text>
                            )}
                          </Td>
                          <Td>
                            {item.share_enable ? (
                              <IconButton
                                icon={<Icon as={ViewIcon} />}
                                size="sm"
                                colorScheme="teal"
                                variant="ghost"
                                aria-label="檢視證件"
                                as="a"
                                href={viewUrl}
                                target="_blank"
                              />
                            ) : (
                              <Text fontSize="s" color="gray.500">連結已停用</Text>
                            )}
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            )}
          </Box>
        </Container>
      </Box>

      {showModel3 && (
        <BlockchainViewerModal
          visible={showModel3}
          tx_block_number={tx_block_number}
          tx_paction_no={tx_paction_no}
          tx_place_no={tx_place_no}
          tx_company_no={tx_company_no}
          tx_block_hash={tx_block_hash}
          tx_TransactionHash={tx_TransactionHash}
          tx_raw_data={tx_raw_data}
          tx_event_type={tx_event_type}
          tx_share_id={tx_share_id}
          tx_open_id={tx_open_id}
          tx_pdf_hash={tx_pdf_hash}
          onClose={() => setShowModel3(false)}
        />
      )}





      {selectedRecord && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px'
          }}
          onClick={() => setSelectedRecord(null)}
        >
          <Box
            bg="white"
            borderRadius="md"
            maxW="480px"
            w="100%"
            maxH="80vh"
            overflow="hidden"
            boxShadow="2xl"
            onClick={(e) => e.stopPropagation()}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
              <Text fontWeight="bold" fontSize="md" color="gray.800">
                開啟紀錄 (共 {selectedRecord.open_records?.length || 0} 次)
              </Text>
              <IconButton
                icon={<CloseIcon />}
                size="sm"
                variant="ghost"
                aria-label="關閉"
                onClick={() => setSelectedRecord(null)}
              />
            </Flex>
            <Box maxH="380px" overflowY="auto" p={4}>
              <Flex direction="column" gap={3}>
                {selectedRecord.open_records?.map((op, idx) => {
                  const hasHash = Boolean(op.pdf_hash);
                  const historicUrl = getHistoricPdfUrl(op.tx_hash, op.log_index);
                  return (
                    <Box key={op.id || idx} p={3} borderRadius="md" bg="gray.50" border="1px solid" borderColor="gray.200">
                      <Flex direction="column" gap={2}>
                        <Text fontWeight="bold" fontSize="sm" color="gray.700">
                          {idx + 1}. 開啟時間：{op.open_time}
                        </Text>
                        <HStack spacing={3} align="center">
                          {op.tx_hash ? (
                            <Link
                              color="teal.600"
                              fontSize="xs"
                              fontWeight="semibold"
                              textDecoration="underline"
                              onClick={() => {
                                setSelectedRecord(null);
                                handleLinkClick(op.tx_hash, op.log_index);
                              }}
                            >
                              TX: {op.tx_hash.substring(0, 14)}...
                            </Link>
                          ) : (
                            <Text fontSize="xs" color="orange.500" fontWeight="bold">待上鏈</Text>
                          )}

                          {op.tx_hash && hasHash ? (
                            <Button
                              as="a"
                              href={historicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="xs"
                              colorScheme="teal"
                              variant="solid"
                              height="24px"
                              fontSize="12px"
                              px={3}
                            >
                              查看當時證件
                            </Button>
                          ) : op.tx_hash && !hasHash ? (
                            <Text fontSize="xs" color="gray.500">無法確認當時版本</Text>
                          ) : null}
                        </HStack>
                      </Flex>
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          </Box>
        </div>
      )}

      <Footer />
    </Flex>
  );
}

export default withAuth(RecipientShareManagement);
